const defenseUrl = new URL('../assets/audio/sfx/combat/defense.mp3', import.meta.url).href;
const slashRecordings = [
  new URL('../assets/audio/sfx/combat/slash/slash-1.mp3', import.meta.url).href,
  new URL('../assets/audio/sfx/combat/slash/slash-2.mp3', import.meta.url).href,
  new URL('../assets/audio/sfx/combat/slash/slash-3.mp3', import.meta.url).href,
  new URL('../assets/audio/sfx/combat/slash/slash-4.mp3', import.meta.url).href,
  new URL('../assets/audio/sfx/combat/slash/slash-5.mp3', import.meta.url).href,
  new URL('../assets/audio/sfx/combat/slash/slash-6.mp3', import.meta.url).href,
];
const sparkSteps = [
  new URL('../assets/audio/sfx/combat/spark step 1.mp3', import.meta.url).href,
  new URL('../assets/audio/sfx/combat/spark step 2.mp3', import.meta.url).href,
];
const initiativeUrl = new URL('../assets/audio/sfx/combat/initiative 1.mp3', import.meta.url).href;
const slashMissUrl = new URL('../assets/audio/sfx/combat/slash_miss.mp3', import.meta.url).href;
const arrowMissUrl = new URL('../assets/audio/sfx/combat/arrow_miss.mp3', import.meta.url).href;
/**
 * Whether Vite bundled this module. Vite rewrites each `import.meta.glob(...)` call but leaves `import.meta.glob` itself undefined, so it cannot be the
 * guard; `import.meta.env` is defined in the browser and absent under the Node test runner, which falls back to the listed files.
 */
const bundled = import.meta.env !== undefined;
const arrowRecordings = bundled
  ? import.meta.glob('../assets/audio/sfx/combat/arrow/*.mp3', { eager: true, query: '?url', import: 'default' }) as Record<string, string>
  : Object.fromEntries([
    'hit 1.mp3', 'hit 2.mp3', 'hit 3.mp3', 'hit 4.mp3',
    'release 1.mp3', 'release 2.mp3', 'release 3.mp3', 'release 4.mp3', 'release 5.mp3', 'release 6.mp3',
  ].map(name => [`../assets/audio/sfx/combat/arrow/${name}`, new URL(`../assets/audio/sfx/combat/arrow/${name}`, import.meta.url).href]));
const arrowClips = (name: 'release' | 'hit') => Object.entries(arrowRecordings)
  .filter(([path]) => path.split('/').at(-1)?.startsWith(`${name} `))
  .sort(([a], [b]) => a.localeCompare(b))
  .map(([, url]) => url);
const arrowReleases = arrowClips('release');
const arrowHits = arrowClips('hit');
const BOW_ATTACKS = new Set(['shortbow', 'bandit-bow']);
/**
 * One-attack recordings: every file in `sfx/combat/arc/` (a Bandit Mage's Arc Lightning discharge), `sfx/combat/sling/` (`release N` as the
 * stone leaves, `hit N` as it lands), `sfx/combat/potion/` (`drink N`) and `sfx/combat/mark/` (`hold N`, a bowstring drawn as Hunter's Mark lands), `sfx/combat/condition/` (`dazed N`, `frightened N`, `prone N`, `taunted N`, as the condition lands), `sfx/combat/command/` (`whistle N`, the Coordinated Strike order), `sfx/combat/rage/` (`burst N` as a Berserker Rages, `heartbeat N` each beat while they Rage), `sfx/combat/tactical/` (`assess N`, `anticipate N`), `sfx/combat/weapon/` (each weapon family's blow, shot and landing, `WEAPON_SOUNDS`), `sling/whirl N` as a sling is whirled up, picked at random; a pool with no file yet stays silent.
 * Under the Node test runner, which cannot glob, the listed files stand in (`tests/combat-pools.test.ts` keeps the lists true).
 */
export const combatPoolFiles = { arc: ['discharge 1.mp3', 'discharge 2.mp3', 'discharge 3.mp3'], sling: ['hit 1.mp3', 'hit 2.mp3', 'hit 3.mp3', 'release 1.mp3', 'release 2.mp3', 'release 3.mp3', 'whirl 1.mp3'], potion: ['drink 1.mp3', 'drink 2.mp3', 'drink 3.mp3'], mark: ['hold 1.mp3'], condition: ['dazed 1.mp3', 'frightened 1.mp3', 'prone 1.mp3', 'taunted 1.mp3'], command: ['whistle 1.mp3'], rage: ['burst 1.mp3', 'heartbeat 1.mp3'], tactical: ['anticipate 1.mp3', 'assess 1.mp3'], weapon: ['axe 1.mp3', 'crossbow hit 1.mp3', 'crossbow hit 2.mp3', 'crossbow release 1.mp3', 'hammer hit 1.mp3', 'hammer hit 2.mp3', 'hammer throw 1.mp3', 'maul 1.mp3', 'polearm 1.mp3', 'polearm 2.mp3', 'spear 1.mp3', 'spear 2.mp3'] } as const;
const poolGlobs = bundled ? {
  arc: import.meta.glob('../assets/audio/sfx/combat/arc/*.mp3', { eager: true, query: '?url', import: 'default' }) as Record<string, string>,
  sling: import.meta.glob('../assets/audio/sfx/combat/sling/*.mp3', { eager: true, query: '?url', import: 'default' }) as Record<string, string>,
  potion: import.meta.glob('../assets/audio/sfx/combat/potion/*.mp3', { eager: true, query: '?url', import: 'default' }) as Record<string, string>,
  mark: import.meta.glob('../assets/audio/sfx/combat/mark/*.mp3', { eager: true, query: '?url', import: 'default' }) as Record<string, string>,
  condition: import.meta.glob('../assets/audio/sfx/combat/condition/*.mp3', { eager: true, query: '?url', import: 'default' }) as Record<string, string>,
  command: import.meta.glob('../assets/audio/sfx/combat/command/*.mp3', { eager: true, query: '?url', import: 'default' }) as Record<string, string>,
  rage: import.meta.glob('../assets/audio/sfx/combat/rage/*.mp3', { eager: true, query: '?url', import: 'default' }) as Record<string, string>,
  tactical: import.meta.glob('../assets/audio/sfx/combat/tactical/*.mp3', { eager: true, query: '?url', import: 'default' }) as Record<string, string>,
  weapon: import.meta.glob('../assets/audio/sfx/combat/weapon/*.mp3', { eager: true, query: '?url', import: 'default' }) as Record<string, string>,
} : Object.fromEntries(Object.entries(combatPoolFiles).map(([folder, names]) => [folder, Object.fromEntries(names.map(name => [`${folder}/${name}`, new URL(`../assets/audio/sfx/combat/${folder}/${name}`, import.meta.url).href]))])) as Record<keyof typeof combatPoolFiles, Record<string, string>>;
const poolClips = (folder: keyof typeof combatPoolFiles, prefix = '') => Object.entries(poolGlobs[folder])
  .filter(([path]) => (path.split('/').at(-1) ?? '').startsWith(prefix))
  .sort(([a], [b]) => a.localeCompare(b))
  .map(([, url]) => url);
const arcDischarges = poolClips('arc'), slingReleases = poolClips('sling', 'release'), slingHits = poolClips('sling', 'hit'), potionDrinks = poolClips('potion'), markHolds = poolClips('mark'), commandWhistles = poolClips('command'), rageBursts = poolClips('rage', 'burst'), heartbeats = poolClips('rage', 'heartbeat'),
  slingWhirls = poolClips('sling', 'whirl'), assessCues = poolClips('tactical', 'assess'), anticipateCues = poolClips('tactical', 'anticipate');
/** The sting of each condition as it lands on its target, by the label the server puts on the event (`condition/dazed N.mp3`). */
const conditionStings: Record<string, readonly string[]> = { DAZED: poolClips('condition', 'dazed'), FRIGHTENED: poolClips('condition', 'frightened'), PRONE: poolClips('condition', 'prone'), TAUNTED: poolClips('condition', 'taunted') };
/**
 * Weapons with their own sound, by attack id: a melee family's `blow` replaces the blade slash, a ranged one's `release` the bowstring and its `hit`
 * the arrow's landing (`weapon/<family> N.mp3`), the blow or landing `accent` dB louder or softer. A family with no recording yet falls back to the slash, bowstring or silence.
 */
const weaponFamily = (prefix: string) => poolClips('weapon', `${prefix} `);
const maulBlows = weaponFamily('maul'), axeBlows = weaponFamily('axe'), spearBlows = weaponFamily('spear'), polearmBlows = weaponFamily('polearm');
const crossbow = { release: weaponFamily('crossbow release'), hit: weaponFamily('crossbow hit') }, thrownHammer = { release: weaponFamily('hammer throw'), hit: weaponFamily('hammer hit'), accent: -3 };
const axe = { blow: axeBlows, accent: -3 };
export const WEAPON_SOUNDS: Record<string, { blow?: readonly string[]; release?: readonly string[]; hit?: readonly string[]; accent?: number }> = {
  greatmaul: { blow: maulBlows }, 'pole-hammer': { blow: maulBlows }, mace: { blow: maulBlows }, club: { blow: maulBlows }, 'bandit-club': { blow: maulBlows },
  'hand-axe': axe, battleaxe: axe, greataxe: axe,
  spear: { blow: spearBlows }, 'great-spear': { blow: spearBlows }, glaive: { blow: polearmBlows },
  crossbow, 'handheld-ballista': crossbow, 'throwing-hammers': thrownHammer,
};
/** The Bandit Mage's bolt. */
const ARC_ATTACKS = new Set(['bandit-arc']);
/**
 * Cantrips and the Songweaver's spell shots have no channel chant and no school voice; each moment has its own pool here instead, filled from
 * `sfx/spells/cantrips/` by the name of the folder holding the clip (`razor wind release/any name.mp3`) or by file-name prefix (`razor wind release 1.wav`). A pool with no recording yet stays silent (see `docs/sounds-wanted.md`).
 */
const cantripRecordings = bundled
  ? import.meta.glob('../assets/audio/sfx/spells/cantrips/**/*.{mp3,wav,flac,ogg}', { eager: true, query: '?url', import: 'default' }) as Record<string, string>
  : {};
const cantripClips = (name: string) => Object.entries(cantripRecordings)
  .filter(([path]) => path.split('/').at(-2) === name || path.split('/').at(-1)?.startsWith(`${name} `))
  .sort(([a], [b]) => a.localeCompare(b))
  .map(([, url]) => url);
const cantripSounds = {
  /** The flat cut of the hand that sends the blade of wind off, and its flight. */
  'razor wind release': cantripClips('razor wind release'),
  /** The wind slicing into its target. */
  'razor wind hit': cantripClips('razor wind hit'),
  /** The slow breath drawn over the palm as the cast begins. */
  'breath of life inhale': cantripClips('breath of life inhale'),
  /** The breath let go, landing on the dying ally as wind and leaves. */
  'breath of life heal': cantripClips('breath of life heal'),
  /** Vicious Mockery's jeer leaving the caster, and its sting on the target. */
  'vicious mockery release': cantripClips('vicious mockery release'),
  'vicious mockery hit': cantripClips('vicious mockery hit'),
  /** The Shepherd: the Lifebinding Spirit arriving and leaving at the fight's end, its Harm landing and its Mend, Rebuke's sear, Withering
   * Touch's decay, Entice's pull and True Strike forming on an ally. */
  'spirit appear': cantripClips('spirit appear'),
  'spirit leave': cantripClips('spirit leave'),
  'spirit harm hit': cantripClips('spirit harm hit'),
  'spirit mend': cantripClips('spirit mend'),
  'rebuke hit': cantripClips('rebuke hit'),
  'withering touch hit': cantripClips('withering touch hit'),
  'entice pull': cantripClips('entice pull'),
  'true strike': cantripClips('true strike'),
  /** The Shadowmancer: a Shadow rising and dissolving, its Shadow Touch, Shadow Blast leaving the hand and landing, and the whisper under
   * Summon and Command Shadows. */
  'shadow rise': cantripClips('shadow rise'),
  'shadow dissolve': cantripClips('shadow dissolve'),
  'shadow touch': cantripClips('shadow touch'),
  'shadow blast release': cantripClips('shadow blast release'),
  'shadow blast hit': cantripClips('shadow blast hit'),
  'summon shadow whisper': cantripClips('summon shadow whisper'),
  'command shadows whisper': cantripClips('command shadows whisper'),
};
/** One moment of a cantrip that has its own pool of recordings. */
export type CantripMoment = keyof typeof cantripSounds;
export type CantripRecordings = Readonly<Record<CantripMoment, readonly string[]>>;
const SPELL_SHOTS: Record<string, CantripMoment> = { 'razor-wind': 'razor wind release', 'vicious-mockery': 'vicious mockery release', 'shadow-blast': 'shadow blast release' };
const SPELL_HITS: Record<string, CantripMoment> = { 'razor-wind': 'razor wind hit', 'vicious-mockery': 'vicious mockery hit', 'spirit-harm': 'spirit harm hit', rebuke: 'rebuke hit', 'withering-touch': 'withering touch hit', 'shadow-blast': 'shadow blast hit' };
/** The whisper as a Shadowmancer starts casting these. */
const CAST_WHISPERS: Record<string, CantripMoment> = { 'summon-shadow': 'summon shadow whisper', 'command-shadows': 'command shadows whisper' };
/** Sneak Attack lands: every recording dropped in `sfx/combat/sneak attack/` (any name) is picked at random; none yet stays silent. */
const sneakAttackRecordings = bundled
  ? Object.values(import.meta.glob('../assets/audio/sfx/combat/sneak attack/*.{mp3,wav,flac,ogg}', { eager: true, query: '?url', import: 'default' }) as Record<string, string>).sort()
  : [];
const movementRecordings = bundled
  ? import.meta.glob('../assets/audio/sfx/movement/*.mp3', { eager: true, query: '?url', import: 'default' }) as Record<string, string>
  : Object.fromEntries(['step_grass-1.mp3', 'step_grass-2.mp3', 'step_road-1.mp3', 'step_road-2.mp3']
    .map(name => [`../assets/audio/sfx/movement/${name}`, new URL(`../assets/audio/sfx/movement/${name}`, import.meta.url).href]));
const movementClips = (terrain: 'grass' | 'road') => Object.entries(movementRecordings)
  .filter(([path]) => path.split('/').at(-1)?.startsWith(`step_${terrain}-`))
  .sort(([a], [b]) => a.localeCompare(b))
  .map(([, url]) => url);
const grassSteps = movementClips('grass');
const roadSteps = movementClips('road');
const wolfBites = [
  new URL('../assets/audio/sfx/monsters/wolf/bite 1.mp3', import.meta.url).href,
  new URL('../assets/audio/sfx/monsters/wolf/bite 2.mp3', import.meta.url).href,
  new URL('../assets/audio/sfx/monsters/wolf/bite 3.mp3', import.meta.url).href,
];
const wolfHowls = [
  new URL('../assets/audio/sfx/monsters/wolf/howl 1.mp3', import.meta.url).href,
  new URL('../assets/audio/sfx/monsters/wolf/howl 2.mp3', import.meta.url).href,
  new URL('../assets/audio/sfx/monsters/wolf/howl 3.mp3', import.meta.url).href,
];
const wolfPains = [
  new URL('../assets/audio/sfx/monsters/wolf/pain 1.mp3', import.meta.url).href,
  new URL('../assets/audio/sfx/monsters/wolf/pain 2.mp3', import.meta.url).href,
];
const wolfDeaths = [
  new URL('../assets/audio/sfx/monsters/wolf/death 1.mp3', import.meta.url).href,
];
/** `lowered` bites are borrowed from a smaller wolf and played at `DIRE_BITE_RATE`, lower and slower so they sound bigger. */
type WolfVoice = { bites: readonly string[]; howls: readonly string[]; pains: readonly string[]; deaths: readonly string[]; lowered?: ReadonlySet<string> };
const DIRE_BITE_RATE = .82;
const forestWolf: WolfVoice = { bites: wolfBites, howls: wolfHowls, pains: wolfPains, deaths: wolfDeaths };
const direWolfClips = (name: string, count: number) => Array.from({ length: count }, (_, i) => new URL(`../assets/audio/sfx/monsters/dire-wolf/${name} ${i + 1}.mp3`, import.meta.url).href);
/** The Dire Wolf and the Pack Leader share one deeper, heavier voice (`monsters/dire-wolf/`); they also bite with the Forest Wolf's bites, lowered. */
const direWolf: WolfVoice = { bites: [...direWolfClips('bite', 1), ...wolfBites], lowered: new Set(wolfBites), howls: direWolfClips('howl', 3), pains: direWolfClips('pain', 3), deaths: direWolfClips('death', 2) };
const DIRE_WOLF_BITES = new Set(['dire-wolf-bite', 'pack-leader-bite']);
const wolfVoice = (unit: EnemyDefinition) => unit.profile.attacks.some(attack => DIRE_WOLF_BITES.has(attack.id)) ? direWolf : forestWolf;
const wolfRecordings = (voice: WolfVoice) => [...new Set([...voice.bites, ...voice.howls, ...voice.pains, ...voice.deaths])];
const goblinVoices = {
  attack: [
    new URL('../assets/audio/sfx/monsters/goblin/attack 1.mp3', import.meta.url).href,
    new URL('../assets/audio/sfx/monsters/goblin/attack 2.mp3', import.meta.url).href,
    new URL('../assets/audio/sfx/monsters/goblin/attack 3.mp3', import.meta.url).href,
    new URL('../assets/audio/sfx/monsters/goblin/attack 4.mp3', import.meta.url).href,
  ],
  engage: [
    new URL('../assets/audio/sfx/monsters/goblin/engage 1.mp3', import.meta.url).href,
    new URL('../assets/audio/sfx/monsters/goblin/engage 2.mp3', import.meta.url).href,
    new URL('../assets/audio/sfx/monsters/goblin/engage 3.mp3', import.meta.url).href,
  ],
  pain: [
    new URL('../assets/audio/sfx/monsters/goblin/pain 1.mp3', import.meta.url).href,
    new URL('../assets/audio/sfx/monsters/goblin/pain 2.mp3', import.meta.url).href,
  ],
  death: [
    new URL('../assets/audio/sfx/monsters/goblin/death 1.mp3', import.meta.url).href,
    new URL('../assets/audio/sfx/monsters/goblin/death 2.mp3', import.meta.url).href,
    new URL('../assets/audio/sfx/monsters/goblin/death 3.mp3', import.meta.url).href,
  ],
  'nice try': [new URL('../assets/audio/sfx/monsters/goblin/nice try.mp3', import.meta.url).href],
  'missed me': [new URL('../assets/audio/sfx/monsters/goblin/haha missed me.mp3', import.meta.url).href],
  'didnt hurt': [new URL('../assets/audio/sfx/monsters/goblin/haha didnt hurt.mp3', import.meta.url).href],
  'gotta do better': [new URL('../assets/audio/sfx/monsters/goblin/gotta do better.mp3', import.meta.url).href],
  'meat shield': [new URL('../assets/audio/sfx/monsters/goblin/meat shield.mp3', import.meta.url).href],
  'get in here': [new URL('../assets/audio/sfx/monsters/goblin/get in here.mp3', import.meta.url).href],
  charge: [new URL('../assets/audio/sfx/monsters/goblin/charge.mp3', import.meta.url).href],
};
/** The Bugbear's own voice for the events it shares with the Goblins; it has no retaliation barks. */
const bugbearVoices = {
  attack: [
    new URL('../assets/audio/sfx/monsters/goblin/bugbear/attack 1.mp3', import.meta.url).href,
    new URL('../assets/audio/sfx/monsters/goblin/bugbear/attack 2.mp3', import.meta.url).href,
  ],
  engage: [
    new URL('../assets/audio/sfx/monsters/goblin/bugbear/engage 1.mp3', import.meta.url).href,
    new URL('../assets/audio/sfx/monsters/goblin/bugbear/engage 2.mp3', import.meta.url).href,
    new URL('../assets/audio/sfx/monsters/goblin/bugbear/engage 3.mp3', import.meta.url).href,
    new URL('../assets/audio/sfx/monsters/goblin/bugbear/engage 4.mp3', import.meta.url).href,
  ],
  pain: [
    new URL('../assets/audio/sfx/monsters/goblin/bugbear/pain 1.mp3', import.meta.url).href,
    new URL('../assets/audio/sfx/monsters/goblin/bugbear/pain 2.mp3', import.meta.url).href,
  ],
  death: [
    new URL('../assets/audio/sfx/monsters/goblin/bugbear/death 1.mp3', import.meta.url).href,
    new URL('../assets/audio/sfx/monsters/goblin/bugbear/death 2.mp3', import.meta.url).href,
  ],
};
type GoblinRole = 'minion' | 'goblin' | 'ratrider' | 'taskmaster' | 'bugbear';
const banditVoices = {
  male: {
    attack: [
      new URL('../assets/audio/sfx/monsters/bandit/male/attack 1.mp3', import.meta.url).href,
      new URL('../assets/audio/sfx/monsters/bandit/male/attack 2.mp3', import.meta.url).href,
      new URL('../assets/audio/sfx/monsters/bandit/male/attack 3.mp3', import.meta.url).href,
    ],
    engage: [
      new URL('../assets/audio/sfx/monsters/bandit/male/engage 1.mp3', import.meta.url).href,
      new URL('../assets/audio/sfx/monsters/bandit/male/engage 2.mp3', import.meta.url).href,
    ],
    pain: [
      new URL('../assets/audio/sfx/monsters/bandit/male/pain 1.mp3', import.meta.url).href,
      new URL('../assets/audio/sfx/monsters/bandit/male/pain 2.mp3', import.meta.url).href,
    ],
    'critical pain': [
      new URL('../assets/audio/sfx/monsters/bandit/male/critical pain 1.mp3', import.meta.url).href,
      new URL('../assets/audio/sfx/monsters/bandit/male/critical pain 2.mp3', import.meta.url).href,
    ],
    inept: [new URL('../assets/audio/sfx/monsters/bandit/male/inept 1.mp3', import.meta.url).href],
    panic: [new URL('../assets/audio/sfx/monsters/bandit/male/panic 1.mp3', import.meta.url).href],
    death: [
      new URL('../assets/audio/sfx/monsters/bandit/male/death 1.mp3', import.meta.url).href,
      new URL('../assets/audio/sfx/monsters/bandit/male/death 2.mp3', import.meta.url).href,
    ],
  },
  female: {
    attack: [
      new URL('../assets/audio/sfx/monsters/bandit/female/attack 1.mp3', import.meta.url).href,
      new URL('../assets/audio/sfx/monsters/bandit/female/attack 2.mp3', import.meta.url).href,
      new URL('../assets/audio/sfx/monsters/bandit/female/attack 3.mp3', import.meta.url).href,
    ],
    engage: [
      new URL('../assets/audio/sfx/monsters/bandit/female/engage 1.mp3', import.meta.url).href,
      new URL('../assets/audio/sfx/monsters/bandit/female/engage 2.mp3', import.meta.url).href,
    ],
    pain: [
      new URL('../assets/audio/sfx/monsters/bandit/female/pain 1.mp3', import.meta.url).href,
      new URL('../assets/audio/sfx/monsters/bandit/female/pain 2.mp3', import.meta.url).href,
    ],
    'critical pain': [
      new URL('../assets/audio/sfx/monsters/bandit/female/critical pain 1.mp3', import.meta.url).href,
      new URL('../assets/audio/sfx/monsters/bandit/female/critical pain 2.mp3', import.meta.url).href,
    ],
    inept: [new URL('../assets/audio/sfx/monsters/bandit/female/inept 1.mp3', import.meta.url).href],
    panic: [new URL('../assets/audio/sfx/monsters/bandit/female/panic 1.mp3', import.meta.url).href],
    death: [
      new URL('../assets/audio/sfx/monsters/bandit/female/death 1.mp3', import.meta.url).href,
      new URL('../assets/audio/sfx/monsters/bandit/female/death 2.mp3', import.meta.url).href,
    ],
  },
};
import { mixer } from './audio-mixer.js';
import { decibels, levelOf, type SoundKind } from './mix.js';
import { CombatVoiceChannel, type CombatVoiceCue } from './combat-voice-channel.js';
import { critterRecordings } from './critter-sounds.js';
import { pingRecordings } from './ping-sounds.js';
import type { WorldSnapshot } from '../../shared/network/protocol.js';
import { firstUnseenEvent } from '../../shared/network/visual-history.js';
import { isShepherdShot } from '../../content/spells/effects.js';
import type { EnemyDefinition, Point } from '../../shared/game/world-types.js';
import type { VoicePlacement } from './voice-director.js';
import { RAGE_PULSE } from '../rendering/combat/rage-pulse.js';
/** How far the scene draws behind the newest snapshot (`GameScene` interpolates 100 ms back). */
const SCENE_LAG_SECONDS = .1;

/** The same cue never repeats faster than this, so several foes striking in the same instant sound like one blow, not a roar. */
const COOLDOWN_MS = 70;
/** A critical blow plays its hit recording about three semitones down. */
const CRIT_RATE = .84;
/** Battle sounds of one kind start at least this far apart: blows landing in the same instant merge, a quick exchange is still heard blow by blow. */
const EFFECT_GAP_MS = 25;
const lastPlayed = new Map<string, number>();
const banditRecordings = Object.values(banditVoices).flatMap(events => Object.values(events).flat());
const goblinRecordings = Object.values(goblinVoices).flat();
const bugbearRecordings = Object.values(bugbearVoices).flat();
const sharedRecordings = () => [defenseUrl, ...sparkSteps, initiativeUrl, ...critterRecordings, ...arcDischarges, ...slingReleases, ...slingHits, ...potionDrinks, ...markHolds, ...commandWhistles, ...rageBursts, ...heartbeats, ...slingWhirls, ...assessCues, ...anticipateCues, ...poolClips('weapon'), ...Object.values(conditionStings).flat(), ...pingRecordings, ...slashRecordings, slashMissUrl, arrowMissUrl, ...arrowReleases, ...arrowHits, ...grassSteps, ...roadSteps, ...Object.values(cantripSounds).flat()];
/** The voices of each monster family among `enemies`; a family with no member in the world is never fetched. */
function monsterRecordings(enemies: readonly EnemyDefinition[]) {
  const urls: string[] = [];
  for (const voice of new Set(enemies.filter(unit => unit.appearance?.rig === 'wolf').map(wolfVoice))) urls.push(...wolfRecordings(voice));
  if (enemies.some(unit => unit.family === 'goblin')) urls.push(...goblinRecordings);
  if (enemies.some(unit => unit.family === 'bugbear')) urls.push(...bugbearRecordings);
  const bandits = enemies.filter(unit => unit.family === 'bandit');
  if (bandits.some(unit => unit.appearance?.rig?.endsWith('-female'))) urls.push(...Object.values(banditVoices.female).flat());
  if (bandits.some(unit => !unit.appearance?.rig?.endsWith('-female'))) urls.push(...Object.values(banditVoices.male).flat());
  return urls;
}
/**
 * Decodes the battle sounds a world can play: the shared ones and the voices of the monster families in `enemies` (summons included), or every
 * recording when no enemies are given (scenarios and tests). The game calls it on entering a world, not in the lobby, so the lobby's backdrop
 * gets the connection; a blow whose sound is not decoded yet is skipped, and the first fight comes long after the world has loaded.
 */
export const preloadCombatSounds = (enemies?: readonly EnemyDefinition[]) =>
  mixer.preloadSfx([...sharedRecordings(), ...enemies ? monsterRecordings(enemies) : [...new Set([...wolfRecordings(forestWolf), ...wolfRecordings(direWolf)]), ...banditRecordings, ...goblinRecordings, ...bugbearRecordings]]);
const between = (low: number, high: number) => low + Math.random() * (high - low);

/** A battle sound through the mix (`mix.ts`): one of a kind per cooldown, so a pack striking together is one blow, not a roar. */
function effect(url: string, kind: SoundKind, strength: number, pan = 0, rate = 1) {
  const now = performance.now();
  if (now - (lastPlayed.get(kind) ?? -Infinity) < EFFECT_GAP_MS) return;
  lastPlayed.set(kind, now);
  mixer.playEffect(url, kind, strength, rate * between(.94, 1.06), pan);
}

function cue(url: string, strength: number, rate = 1, key = url, cooldown = COOLDOWN_MS, pan = 0) {
  const now = performance.now();
  if (now - (lastPlayed.get(key) ?? -Infinity) < cooldown) return;
  lastPlayed.set(key, now);
  mixer.playSfx(url, strength, undefined, rate * between(.94, 1.06), false, pan);
}

/** One footfall, on the road or on grass; `strength` (0..1) falls with distance from the camera and `pan` follows the walker across the screen. */
export const playStep = (strength: number, onRoad = false, quadruped = false, paw = 'shared', pan = 0) => {
  const terrain = onRoad ? 'road' : 'grass', urls = onRoad ? roadSteps : grassSteps;
  cue(urls[Math.floor(Math.random() * urls.length)], (quadruped ? .16 : .275) * strength * 2,
    between(.92, 1.08) * (quadruped ? 1.18 : 1) * .9, quadruped ? `paw:${paw}` : `step:${terrain}`, quadruped ? 20 : COOLDOWN_MS, pan);
};

/**
 * Turns the server's visual events into combat sounds. Like the animation cues it ignores history on join or restart.
 * Slashes sound at the swing and become a whiff when the same swing is reported as a miss; a bow sounds as the arrow is loosed
 * and a whistling miss is heard when it lands wide; a block or an interposing ally sounds as the defense. Every sound comes from where it
 * happens (`place`): a swing from the attacker, a hit or a block from the one struck, a bark from the one who speaks.
 */
export class CombatSounds {
  private wolves: Map<string, WolfVoice>;
  private bandits = new Map<string, typeof banditVoices[keyof typeof banditVoices]>();
  private banditTargets = new Map<string, string | null>();
  private goblins = new Map<string, GoblinRole>();
  private goblinTargets = new Map<string, string | null>();
  private factions = new Map<string, string>();
  private voiceChannel: CombatVoiceChannel;
  /** How loud a sound at a world point is for the listener and which side of the screen it is on; null hears it centred at full strength. */
  place: (at: Point) => VoicePlacement | null = () => null;
  /** Where each unit stood in the latest snapshot. */
  private where = new Map<string, Point>();
  constructor(definitions: readonly EnemyDefinition[] = [], private play: (url: string, strength: number, pan: number, kind: SoundKind, rate?: number) => void = (url, strength, pan, kind, rate) => effect(url, kind, strength, pan, rate),
    private random: () => number = Math.random, now: () => number = () => performance.now() / 1000,
    duration: (url: string) => number = url => mixer.sfxDuration(url), private cantrips: CantripRecordings = cantripSounds) {
    this.wolves = new Map(definitions.filter(unit => unit.appearance?.rig === 'wolf').map(unit => [unit.id, wolfVoice(unit)]));
    this.voiceChannel = new CombatVoiceChannel((url, strength, pan = 0) => this.play(url, strength, pan, 'bark'), duration, now);
    for (const unit of definitions) {
      this.factions.set(unit.id, unit.factionId ?? 'hostiles');
      if (unit.family === 'bandit') {
        this.bandits.set(unit.id, unit.appearance?.rig?.endsWith('-female') ? banditVoices.female : banditVoices.male);
      } else if (unit.family === 'goblin' || unit.family === 'bugbear') {
        const traits = unit.profile.traits ?? [];
        const role: GoblinRole = unit.family === 'bugbear' ? 'bugbear' : unit.kind === 'minion' ? 'minion'
          : unit.appearance?.rig === 'goblin-ratrider' ? 'ratrider'
          : traits.some(trait => trait.id === 'summon-after-attack') ? 'taskmaster' : 'goblin';
        this.goblins.set(unit.id, role);
      }
    }
  }
  private recording(urls: readonly string[]) { return urls[Math.min(urls.length - 1, Math.floor(this.random() * urls.length))]; }
  /** A sound at a unit's place (or at `at` when the unit is unknown), quieter with distance and panned to its side of the screen. */
  private placed(unitId: string | undefined, at?: Point): VoicePlacement {
    const point = (unitId ? this.where.get(unitId) : undefined) ?? at;
    return (point && this.place(point)) || { strength: 1, pan: 0 };
  }
  /** A sound of `kind` at its level in the mix, `accent` dB louder or softer. */
  private sound(url: string, kind: SoundKind, unitId?: string, at?: Point, accent = 0, rate = 1) {
    const { strength: reach, pan } = this.placed(unitId, at);
    if (reach > 0) this.play(url, levelOf(kind) * decibels(accent) * reach, pan, kind, rate);
  }
  /** One recording of a pool at its kind's level, or nothing while the pool is empty. */
  private pooled(urls: readonly string[], kind: SoundKind, unitId?: string, at?: Point, accent = 0) {
    if (urls.length) this.sound(this.recording(urls), kind, unitId, at, accent);
  }
  /** A critical blow: the ordinary hit recording, louder and played slower so it lands deeper and heavier. */
  private blow(url: string, critical: boolean, unitId?: string, at?: Point, accent = 0, rate = 1) {
    if (critical) this.sound(url, 'crit', unitId, at, accent, rate * CRIT_RATE); else this.sound(url, 'hit', unitId, at, accent, rate);
  }
  private bark(cues: CombatVoiceCue[], id: string, url: string, strength: number, priority: number) {
    const { strength: reach, pan } = this.placed(id);
    if (reach > 0) cues.push({ url, strength: levelOf('bark') * strength * reach, priority, factionId: this.faction(id), pan });
  }
  /** Plays one recording of a cantrip moment, or nothing while it has none. */
  private cantrip(moment: CantripMoment, unitId?: string, at?: Point) {
    const urls = this.cantrips[moment], kind: SoundKind = moment.startsWith('breath') ? 'breath' : moment === 'spirit appear' || moment === 'spirit leave' ? 'spirit'
      : moment === 'shadow rise' || moment === 'shadow dissolve' ? 'shadow' : moment.endsWith('whisper') ? 'shadow-whisper' : moment.endsWith('release') ? 'cantrip-release' : 'cantrip-hit';
    // The inhale is the quiet half of the breath.
    if (urls.length) this.sound(this.recording(urls), kind, unitId, at, moment === 'breath of life inhale' ? -3 : 0);
  }
  /**
   * The scenario viewer's cosmetic cantrip casts, which never reach the server: `begin` as the cast starts, `release` as it leaves the hand
   * and `land` as it reaches its target, each sounding as the real cast does at that moment.
   */
  previewCantrip(abilityId: string, moment: 'begin' | 'release' | 'land', at?: Point) {
    if (abilityId === 'breath-of-life') { if (moment === 'begin') this.cantrip('breath of life inhale', undefined, at); else if (moment === 'release') this.cantrip('breath of life heal', undefined, at); return; }
    const pool = moment === 'release' ? SPELL_SHOTS[abilityId] : moment === 'land' ? SPELL_HITS[abilityId] : undefined;
    if (pool) this.cantrip(pool, undefined, at);
  }
  /** The scenario viewer's cosmetic monster-ability previews: a goblin's "Haha!" stings its target as the real retaliation does. */
  previewAbility(abilityId: string, at?: Point) { if (abilityId === 'survival-retaliation') this.cantrip('vicious mockery hit', undefined, at); }
  /** The cantrip each hero was last seen casting, so a cast is heard once, as it begins. */
  private casting = new Map<string, string | null>();
  /** Units last seen winding up a sling throw. */
  private whirling = new Set<string>();
  /** The Shadowmancers' Shadows last seen standing, and where. */
  private shadows = new Map<string, Point>();
  private livingShadows(snapshot: WorldSnapshot) {
    const shadows = new Set((snapshot.summonedEnemies ?? []).filter(unit => unit.appearance?.rig === 'shadow').map(unit => unit.id));
    return new Map(snapshot.enemies.filter(unit => shadows.has(unit.id) && !unit.health.dead).map(unit => [unit.id, { x: unit.x, z: unit.z }]));
  }
  /** Heroes last seen with their Lifebinding Spirit at their side. */
  private spirits = new Set<string>();
  private faction(id: string) { return this.factions.get(id) ?? 'hostiles'; }
  private banditSound(id: string, event: keyof typeof banditVoices.male, strength: number, cues: CombatVoiceCue[]) {
    const recordings = this.bandits.get(id)?.[event];
    if (!recordings) return;
    const priority = event === 'death' ? 5 : event === 'critical pain' ? 4 : event === 'engage' ? 3 : event === 'pain' || event === 'inept' || event === 'panic' ? 2 : 1;
    this.bark(cues, id, this.recording(recordings), strength, priority);
  }
  private goblinSound(id: string, event: keyof typeof goblinVoices, strength: number, cues: CombatVoiceCue[]) {
    if (!this.goblins.has(id)) return;
    const bugbear = this.goblins.get(id) === 'bugbear' && event in bugbearVoices;
    const url = this.recording(bugbear ? bugbearVoices[event as keyof typeof bugbearVoices] : goblinVoices[event]);
    const priority = event === 'death' ? 5 : event === 'engage' || event === 'meat shield' || event === 'get in here' ? 3
      : event === 'pain' || event === 'nice try' || event === 'missed me' || event === 'didnt hurt' || event === 'gotta do better' ? 2 : 1;
    this.bark(cues, id, url, strength, priority);
  }
  private revision = -1;
  private eventId = 0;
  private projectileId = 0;
  /** Whether each unit last attacked from range, so its next miss sounds like an arrow and not a blade. */
  private ranged = new Map<string, boolean>();
  /** The pulse time each Raging hero was last heard at, so a beat sounds once as its thump comes round. */
  private pulses = new Map<string, number>();

  /**
   * A Raging hero's heart, beating with the flush on their skin (`RAGE_PULSE`): a double thump each time the pulse comes round, quicker with
   * each Fury Die. The pulse is read on the scene's time, which runs `SCENE_LAG_SECONDS` behind the newest snapshot, so the thump lands as the skin throbs.
   */
  private heartbeats(snapshot: WorldSnapshot) {
    const time = snapshot.gameTime - SCENE_LAG_SECONDS, raging = new Set<string>();
    for (const hero of snapshot.heroes) {
      const fury = hero.class.fury;
      if (!fury?.raging || hero.health.dead) continue;
      raging.add(hero.id);
      const before = this.pulses.get(hero.id), rate = RAGE_PULSE.rate(fury.dice.length), offset = RAGE_PULSE.seed(hero.id) - RAGE_PULSE.thump;
      this.pulses.set(hero.id, time);
      if (before !== undefined && Math.floor(time * rate + offset) > Math.floor(before * rate + offset)) this.pooled(heartbeats, 'heartbeat', hero.id);
    }
    for (const id of this.pulses.keys()) if (!raging.has(id)) this.pulses.delete(id);
  }

  receive(snapshot: WorldSnapshot) {
    this.where.clear();
    for (const unit of [...snapshot.heroes, ...snapshot.enemies]) this.where.set(unit.id, { x: unit.x, z: unit.z });
    if (snapshot.revision !== this.revision) {
      this.revision = snapshot.revision; this.ranged.clear(); this.casting.clear(); this.pulses.clear();
      for (const hero of snapshot.heroes) this.casting.set(hero.id, hero.class.casting?.abilityId ?? null);
      this.spirits = new Set(snapshot.heroes.filter(hero => hero.class.companion).map(hero => hero.id));
      this.shadows = this.livingShadows(snapshot);
      this.whirling = new Set([...snapshot.heroes, ...snapshot.enemies].filter(unit => unit.combat?.windup?.attackId === 'sling').map(unit => unit.id));
      this.banditTargets.clear();
      this.goblinTargets.clear();
      for (const enemy of snapshot.enemies) if (this.bandits.has(enemy.id)) this.banditTargets.set(enemy.id, enemy.combat.targetId);
      for (const enemy of snapshot.enemies) if (this.goblins.has(enemy.id)) this.goblinTargets.set(enemy.id, enemy.combat.targetId);
      this.eventId = firstUnseenEvent(snapshot);
      this.projectileId = Math.max(0, ...snapshot.projectiles.map(shot => shot.id));
      return;
    }
    const voiceCues: CombatVoiceCue[] = [];
    for (const enemy of snapshot.enemies) {
      if (this.bandits.has(enemy.id)) {
        const previousTarget = this.banditTargets.get(enemy.id);
        if (enemy.combat.targetId && !previousTarget) this.banditSound(enemy.id, 'engage', .8, voiceCues);
        this.banditTargets.set(enemy.id, enemy.combat.targetId);
      }
      if (this.goblins.has(enemy.id)) {
        const previousTarget = this.goblinTargets.get(enemy.id);
        if (enemy.combat.targetId && !previousTarget) this.goblinSound(enemy.id, 'engage', .8, voiceCues);
        this.goblinTargets.set(enemy.id, enemy.combat.targetId);
      }
    }
    for (const id of this.banditTargets.keys()) if (!snapshot.enemies.some(enemy => enemy.id === id)) this.banditTargets.delete(id);
    for (const id of this.goblinTargets.keys()) if (!snapshot.enemies.some(enemy => enemy.id === id)) this.goblinTargets.delete(id);
    // A sling is whirled up as its throw winds up, before the stone leaves.
    for (const unit of [...snapshot.heroes, ...snapshot.enemies]) {
      const sling = unit.combat?.windup?.attackId === 'sling';
      if (sling && !this.whirling.has(unit.id)) this.pooled(slingWhirls, 'swing', unit.id);
      if (sling) this.whirling.add(unit.id); else this.whirling.delete(unit.id);
    }
    for (const hero of snapshot.heroes) {
      const ability = hero.class.casting?.abilityId ?? null;
      if (ability === 'breath-of-life' && this.casting.get(hero.id) !== ability) this.cantrip('breath of life inhale', hero.id);
      const whisper = CAST_WHISPERS[ability ?? ''];
      if (whisper && this.casting.get(hero.id) !== ability) this.cantrip(whisper, hero.id);
      this.casting.set(hero.id, ability);
      // The Lifebinding Spirit leaves as the fight ends (its arrival is the server's `ability` event).
      if (this.spirits.has(hero.id) && !hero.class.companion) this.cantrip('spirit leave', hero.id);
      if (hero.class.companion) this.spirits.add(hero.id); else this.spirits.delete(hero.id);
    }
    // A Shadow rises as it joins the fight and dissolves as it falls or the fight ends, where it last stood.
    const shadows = this.livingShadows(snapshot);
    for (const [id, at] of shadows) if (!this.shadows.has(id)) this.cantrip('shadow rise', id, at);
    for (const [id, at] of this.shadows) if (!shadows.has(id)) this.cantrip('shadow dissolve', undefined, at);
    this.shadows = shadows;
    for (const shot of snapshot.projectiles) if (shot.id > this.projectileId) {
      const spell = SPELL_SHOTS[shot.attackId];
      if (spell) { this.ranged.set(shot.actorId, false); this.cantrip(spell, shot.actorId, shot.from); continue; }
      // The Shepherd's bolts sound only as they land: never a bowstring.
      if (isShepherdShot(shot.attackId)) { this.ranged.set(shot.actorId, false); continue; }
      this.ranged.set(shot.actorId, true);
      if (ARC_ATTACKS.has(shot.attackId)) this.pooled(arcDischarges, 'arc', shot.actorId, shot.from);
      else if (shot.attackId === 'sling') this.pooled(slingReleases, 'swing', shot.actorId, shot.from);
      else if (WEAPON_SOUNDS[shot.attackId]?.release) this.pooled(WEAPON_SOUNDS[shot.attackId].release!, 'swing', shot.actorId, shot.from);
      else this.sound(this.recording(arrowReleases), 'swing', shot.actorId, shot.from);
      this.banditSound(shot.actorId, 'attack', .75, voiceCues);
      this.goblinSound(shot.actorId, 'attack', .75, voiceCues);
    }
    this.projectileId = Math.max(this.projectileId, ...snapshot.projectiles.map(shot => shot.id));
    const fresh = snapshot.visualEvents.filter(event => event.id > this.eventId);
    let drums = false;
    for (const event of fresh) {
      if (event.kind === 'strike') {
        this.ranged.set(event.actorId, false);
        const whiffs = fresh.some(other => other.id > event.id && other.kind === 'miss' && other.actorId === event.actorId && other.targetId === event.targetId);
        const critical = fresh.some(other => other.id > event.id && other.kind === 'impact' && other.critical && other.actorId === event.actorId && other.targetId === event.targetId);
        if (this.wolves.has(event.actorId)) {
          const voice = this.wolves.get(event.actorId)!, bite = this.recording(voice.bites);
          this.blow(bite, critical, event.actorId, event.from, 0, voice.lowered?.has(bite) ? DIRE_BITE_RATE : 1);
        }
        else if (this.shadows.has(event.actorId)) this.cantrip('shadow touch', event.actorId, event.from);
        else {
          const weapon = WEAPON_SOUNDS[event.attackId ?? ''];
          if (whiffs) this.sound(slashMissUrl, 'miss', event.actorId, event.from);
          else if (weapon?.blow?.length) this.blow(this.recording(weapon.blow), critical, event.actorId, event.from, weapon.accent);
          else this.blow(this.recording(slashRecordings), critical, event.actorId, event.from);
          this.banditSound(event.actorId, 'attack', .75, voiceCues);
          this.goblinSound(event.actorId, 'attack', .75, voiceCues);
        }
      } else if (event.kind === 'impact') {
        if (event.sneakAttack && sneakAttackRecordings.length) this.sound(this.recording(sneakAttackRecordings), 'heavy-hit', event.targetId, event.to);
        if (this.bandits.has(event.targetId) && event.hpDamage) {
          this.banditSound(event.targetId, event.lethal ? 'death' : event.critical ? 'critical pain' : 'pain', .8, voiceCues);
        }
        if (this.goblins.has(event.targetId) && event.hpDamage) this.goblinSound(event.targetId, event.lethal ? 'death' : 'pain', .8, voiceCues);
        if (this.wolves.has(event.targetId) && event.hpDamage) { const voice = this.wolves.get(event.targetId)!; this.bark(voiceCues, event.targetId, this.recording(event.lethal ? voice.deaths : voice.pains), .8, event.lethal ? 5 : 2); }
        if (event.ranged && BOW_ATTACKS.has(event.attackId ?? '')) this.blow(this.recording(arrowHits), event.critical, event.targetId, event.to);
        else if (event.ranged && event.attackId === 'sling' && slingHits.length) this.blow(this.recording(slingHits), event.critical, event.targetId, event.to);
        else if (event.ranged && WEAPON_SOUNDS[event.attackId ?? '']?.hit?.length) this.blow(this.recording(WEAPON_SOUNDS[event.attackId!].hit!), event.critical, event.targetId, event.to, WEAPON_SOUNDS[event.attackId!].accent);
        const spell = SPELL_HITS[event.attackId ?? ''];
        if (spell) this.cantrip(spell, event.targetId, event.to);
      } else if (event.kind === 'heal' && event.abilityId === 'breath-of-life') {
        this.cantrip('breath of life heal', event.targetId, event.to);
      } else if (event.kind === 'heal' && event.abilityId === 'spirit-mend') {
        this.cantrip('spirit mend', event.targetId, event.to);
      } else if (event.kind === 'heal' && !event.abilityId && !event.label) {
        // A healing potion drunk (Breath of Life and Relentless are other heals).
        this.pooled(potionDrinks, 'potion', event.targetId, event.to);
      } else if (event.kind === 'command') {
        // Coordinated Strike: the order is a whistle, heard where the leader stands.
        this.pooled(commandWhistles, 'command', event.actorId, event.from);
      } else if (event.kind === 'mark') {
        // Hunter's Mark: the bowstring drawn and held at half volume, heard where the hunter stands.
        this.pooled(markHolds, 'swing', event.actorId, event.from, 20 * Math.log10(.5));
      } else if (event.kind === 'arc') {
        // The jump to a second target: the same crack, quieter, where it lands.
        this.pooled(arcDischarges, 'arc', undefined, event.to, -6);
      } else if (event.kind === 'teleport') {
        // Heard where the monster stood: by the time the event arrives it is already on the far side.
        this.sound(this.recording(sparkSteps), 'spark-step', undefined, event.from);
      } else if (event.kind === 'assess') {
        this.pooled(assessCues, 'condition', event.actorId, event.from);
      } else if (event.kind === 'anticipate') {
        this.pooled(anticipateCues, 'condition', event.actorId, event.from);
      } else if (event.kind === 'initiative') {
        drums = true;
      } else if (event.kind === 'condition' && conditionStings[event.label ?? '']) {
        this.pooled(conditionStings[event.label ?? ''], 'condition', event.targetId, event.to);
      } else if (event.kind === 'condition' && event.label === 'FEARSOME HOWL' && this.wolves.has(event.actorId)) {
        this.bark(voiceCues, event.actorId, this.recording(this.wolves.get(event.actorId)!.howls), .9, 3);
      } else if (event.kind === 'ability' && event.abilityId === 'survival-retaliation') {
        // A goblin's "Haha!" stings the one who hit it like Vicious Mockery does.
        this.cantrip('vicious mockery hit', event.targetId, event.to);
        const retaliation = this.goblins.get(event.actorId);
        if (!retaliation) continue;
        const bark = retaliation === 'minion' ? 'missed me' : retaliation === 'ratrider' ? 'nice try'
          : retaliation === 'taskmaster' ? 'gotta do better' : 'didnt hurt';
        this.goblinSound(event.actorId, bark, .85, voiceCues);
      } else if (event.kind === 'ability' && event.abilityId === 'lifebinding-spirit') {
        this.cantrip('spirit appear', event.actorId, event.from);
      } else if (event.kind === 'ability' && event.abilityId === 'true-strike') {
        this.cantrip('true strike', event.targetId, event.to);
      } else if (event.kind === 'condition' && event.abilityId === 'entice') {
        // Dragged toward the Shepherd: heard where the target ends up.
        this.cantrip('entice pull', event.targetId, event.to);
      } else if (event.kind === 'ability' && event.abilityId === 'rage') {
        // God's Strength: the fire bursting up round the Berserker.
        this.pooled(rageBursts, 'rage-burst', event.actorId, event.from);
      } else if (event.kind === 'ability' && event.abilityId === 'charge' && this.goblins.get(event.actorId) === 'ratrider') {
        this.goblinSound(event.actorId, 'charge', .9, voiceCues);
      } else if (event.kind === 'ability' && event.abilityId === 'summon-after-attack') {
        this.goblinSound(event.actorId, 'get in here', .9, voiceCues);
      } else if (event.kind === 'interpose') {
        if (event.abilityId === 'meat-shield') this.goblinSound(event.targetId, 'meat shield', .9, voiceCues);
        this.sound(defenseUrl, 'block', event.targetId, event.to);
      } else if (event.kind === 'miss') {
        if (event.passive) this.banditSound(event.passive === 'inept' ? event.actorId : event.targetId, event.passive, .8, voiceCues);
        if (this.ranged.get(event.actorId)) this.sound(arrowMissUrl, 'miss', event.targetId, event.to);
      } else if (event.kind === 'block') this.sound(defenseUrl, 'block', event.targetId, event.to);
    }
    // Every hero rolls Initiative as a fight begins: one drumbeat for the party, heard centred.
    if (drums) this.play(initiativeUrl, levelOf('initiative'), 0, 'initiative');
    this.heartbeats(snapshot);
    this.voiceChannel.playOne(voiceCues);
    this.eventId = Math.max(this.eventId, ...fresh.map(event => event.id));
  }
}
